import React, { useRef } from 'react';
import { useT } from '@/i18n/labels';
import type { TimelineCounts, TimelineFilter as FilterValue } from '@/utils/timeline';

const VALUES: readonly FilterValue[] = ['all', 'chat', 'consult'];

/**
 * The segmented filter over the merged timeline: All / Customer chat / Consultation, each with a count.
 *
 * It narrows what is SHOWN and nothing else. Where the answer bar sends is decided by the conversation's
 * state and owner (AnswerBar), never by this control - viewing the consultation does not make the next
 * message a guidance note, and viewing the customer chat does not make it a customer message.
 *
 * It is a CONTROLLED, stateless control: the value lives in the chat view, local to the open conversation. It
 * starts at All and resets to All when another conversation is opened; nothing is remembered between
 * conversations or between visits (no localStorage).
 *
 * A radiogroup (one choice, always exactly one selected) with a roving tabindex: Tab enters on the selected
 * segment, the arrow keys move and select.
 */
export const TimelineFilter: React.FC<{
  value: FilterValue;
  counts: TimelineCounts;
  onChange: (f: FilterValue) => void;
  /**
   * True while the bot is waiting for help on this conversation. Puts a dot on the Consultation segment so
   * the request is not invisible when the agent is looking at "Customer chat".
   */
  consulting?: boolean;
}> = ({ value, counts, onChange, consulting = false }) => {
  const { t } = useT();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const label = (f: FilterValue) =>
    f === 'all' ? t('tl_filter_all') : f === 'chat' ? t('tl_filter_chat') : t('tl_filter_consult');

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next = index;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (index + 1) % VALUES.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (index - 1 + VALUES.length) % VALUES.length;
    else return;
    e.preventDefault();
    onChange(VALUES[next]);
    refs.current[VALUES[next]]?.focus();
  };

  return (
    <div className="ac-tlf" role="radiogroup" aria-label={t('tl_filter_aria')} data-testid="timeline-filter">
      {VALUES.map((f, i) => {
        const on = value === f;
        return (
          <button
            key={f}
            ref={(el) => {
              refs.current[f] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            className={`ac-tlf-btn ac-tlf-${f}` + (on ? ' on' : '')}
            onClick={() => onChange(f)}
            onKeyDown={(e) => onKeyDown(e, i)}
            data-testid={`timeline-filter-${f}`}
          >
            {label(f)}
            {f === 'consult' && consulting && (
              <span className="ac-tlf-dot" aria-hidden="true" data-testid="timeline-filter-consult-dot" />
            )}
            <span className="ac-tlf-n" data-testid={`timeline-filter-count-${f}`}>
              {counts[f]}
            </span>
          </button>
        );
      })}
    </div>
  );
};
